"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import type { COULOMBS_LAW_CALCULATOR_STRINGS } from "@/components/tools/coulombs-law-calculator";
import {
  PairDiagram,
  RadialDiagram,
} from "@/components/tools/physics-fields/coulomb-diagrams";
import {
  carrySolved,
  DiagramFrame,
  type SolveFieldSpec,
  SolveGrid,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import { useToolNumber } from "@/components/tools/tool-ui";
import { BOHR_RADIUS } from "@/lib/tool-kit/physics-fields/constants";
import {
  type CoulombError,
  type CoulombTarget,
  type FieldTarget,
  solveCoulomb,
  solvePointField,
} from "@/lib/tool-kit/physics-fields/coulomb";
import {
  coulombWorking,
  pointFieldWorking,
} from "@/lib/tool-kit/physics-fields/coulomb-working";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readPlain, readQuantity } from "@/lib/tool-kit/physics-fields/input";
import {
  CHARGE_UNITS,
  FIELD_UNITS,
  SMALL_DISTANCE_UNITS,
  SMALL_FORCE_UNITS,
} from "@/lib/tool-kit/physics-fields/units";

type Strings = typeof COULOMBS_LAW_CALCULATOR_STRINGS;

export interface PanelProps {
  strings: Strings;
  sig: number;
}

/** Relative permittivities (dielectric constants) near 20 °C and 1 atm, CRC Handbook. */
export const MEDIA: Record<string, number> = {
  vacuum: 1,
  air: 1.000_59,
  water: 80.2,
};

export function Failure({
  error,
  strings,
}: {
  error: CoulombError;
  strings: Strings;
}) {
  const messages: Record<CoulombError, string> = {
    missing: strings.errorMissing,
    "non-positive": strings.errorPositive,
    "zero-charge": strings.errorZeroCharge,
    "zero-force": strings.errorZeroForce,
    "sign-mismatch": strings.errorSign,
    coincident: strings.errorCoincident,
    "not-finite": strings.errorRange,
  };
  return error === "missing" ? (
    <Notice>{messages.missing}</Notice>
  ) : (
    <ErrorNotice>{messages[error]}</ErrorNotice>
  );
}

/** The medium menu and its εᵣ box. */
export function MediumFields({
  strings,
  idPrefix,
  medium,
  eps,
  onMedium,
  onEps,
}: {
  strings: Strings;
  idPrefix: string;
  medium: string;
  eps: string;
  onMedium: (id: string) => void;
  onEps: (text: string) => void;
}) {
  const names = strings.mediumNames as Record<string, string>;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <SelectField
        id={`${idPrefix}-medium`}
        label={strings.mediumLabel}
        onChange={onMedium}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(MEDIA).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={medium}
      />
      <TextField
        hint={strings.permittivityHint}
        id={`${idPrefix}-eps`}
        label={strings.permittivity}
        numeric
        onChange={onEps}
        value={eps}
      />
    </div>
  );
}

function natureLabel(
  nature: "repulsive" | "attractive" | "none",
  strings: Strings
): string {
  const labels = {
    repulsive: strings.repulsive,
    attractive: strings.attractive,
    none: strings.noForce,
  };
  return labels[nature];
}

/* ── Force between two charges ────────────────────────────────────────────── */

type PairKey = "F" | "a" | "b" | "r";

const PAIR_EXAMPLES: Record<
  string,
  { text: Record<PairKey, string>; unit: Record<PairKey, string> }
> = {
  micro: {
    text: { F: "", a: "1", b: "1", r: "1" },
    unit: { F: "mN", a: "uC", b: "uC", r: "m" },
  },
  nano: {
    text: { F: "", a: "2", b: "-3", r: "5" },
    unit: { F: "uN", a: "nC", b: "nC", r: "cm" },
  },
  hydrogen: {
    text: { F: "", a: "1", b: "-1", r: String(BOHR_RADIUS) },
    unit: { F: "nN", a: "e", b: "e", r: "m" },
  },
};

export function PairPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [example, setExample] = useState("micro");
  const [target, setTarget] = useState<CoulombTarget>("force");
  const [medium, setMedium] = useState("vacuum");
  const [eps, setEps] = useState("1");
  const [text, setText] = useState<Record<PairKey, string>>(
    PAIR_EXAMPLES.micro.text
  );
  const [unit, setUnit] = useState<Record<PairKey, string>>(
    PAIR_EXAMPLES.micro.unit
  );

  const outcome = solveCoulomb(
    {
      force: readQuantity(text.F, SMALL_FORCE_UNITS, unit.F),
      q1: readQuantity(text.a, CHARGE_UNITS, unit.a),
      q2: readQuantity(text.b, CHARGE_UNITS, unit.b),
      distance: readQuantity(text.r, SMALL_DISTANCE_UNITS, unit.r),
      permittivity: readPlain(eps),
    },
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<PairKey>[] = [
    {
      key: "a",
      label: strings.charge1,
      units: CHARGE_UNITS,
      solved: target === "q1",
      hint: strings.chargeHint,
    },
    {
      key: "b",
      label: strings.charge2,
      units: CHARGE_UNITS,
      solved: target === "q2",
    },
    {
      key: "r",
      label: strings.distance,
      units: SMALL_DISTANCE_UNITS,
      solved: target === "distance",
      hint: strings.distanceHint,
    },
    {
      key: "F",
      label: strings.force,
      units: SMALL_FORCE_UNITS,
      solved: target === "force",
      hint: strings.forceHint,
    },
  ];
  const solvedSI = {
    a: value?.q1 ?? null,
    b: value?.q2 ?? null,
    r: value?.distance ?? null,
    F: value?.force ?? null,
  };
  const names = strings.pairExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = PAIR_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setUnit(ex.unit);
      setTarget("force");
      setMedium("vacuum");
      setEps("1");
    }
  };
  const pickMedium = (id: string) => {
    setMedium(id);
    if (id in MEDIA) {
      setEps(String(MEDIA[id]));
    }
  };

  return (
    <>
      <SelectField
        id="cl-pair-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(PAIR_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <PillGroup
        label={strings.solveFor}
        name="cl-pair-solve"
        onChange={(next: CoulombTarget) => {
          setText(carrySolved(specs, text, unit, solvedSI, sig));
          setTarget(next);
        }}
        options={[
          { value: "force", label: strings.forceShort },
          { value: "q1", label: strings.charge1Short },
          { value: "q2", label: strings.charge2Short },
          { value: "distance", label: strings.distanceShort },
        ]}
        value={target}
      />
      <MediumFields
        eps={eps}
        idPrefix="cl-pair"
        medium={medium}
        onEps={(v) => {
          setMedium("custom");
          setEps(v);
        }}
        onMedium={pickMedium}
        strings={strings}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="cl-pair"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={solvedSI}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.forceSigned}
                value={`${formatSig(fmt, value.force, sig)} N`}
              />
              <StatCard
                label={strings.forceMagnitude}
                value={`${formatSig(fmt, Math.abs(value.force), sig)} N`}
              />
              <StatCard
                label={strings.theForceIs}
                value={natureLabel(value.nature, strings)}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={coulombWorking(target, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.pairCaption}>
              <PairDiagram
                description={strings.pairDescription}
                force={value.force}
                id="cl-pair-diagram"
                label={strings.pairLabel}
                q1={value.q1}
                q2={value.q2}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}

/* ── Field of one charge ──────────────────────────────────────────────────── */

type FieldKey = "E" | "q" | "r";

const FIELD_EXAMPLES: Record<
  string,
  {
    text: Record<FieldKey, string>;
    unit: Record<FieldKey, string>;
    negative: boolean;
  }
> = {
  micro: {
    text: { E: "", q: "1", r: "0.5" },
    unit: { E: "kN/C", q: "uC", r: "m" },
    negative: false,
  },
  hydrogen: {
    text: { E: "", q: "1", r: String(BOHR_RADIUS) },
    unit: { E: "N/C", q: "e", r: "m" },
    negative: false,
  },
};

export function PointFieldPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [example, setExample] = useState("micro");
  const [target, setTarget] = useState<FieldTarget>("field");
  const [negative, setNegative] = useState(false);
  const [medium, setMedium] = useState("vacuum");
  const [eps, setEps] = useState("1");
  const [text, setText] = useState<Record<FieldKey, string>>(
    FIELD_EXAMPLES.micro.text
  );
  const [unit, setUnit] = useState<Record<FieldKey, string>>(
    FIELD_EXAMPLES.micro.unit
  );

  const chargeSI = readQuantity(text.q, CHARGE_UNITS, unit.q);
  const outcome = solvePointField(
    {
      field: readQuantity(text.E, FIELD_UNITS, unit.E),
      charge:
        chargeSI === null || target === "charge"
          ? chargeSI
          : (negative ? -1 : 1) * Math.abs(chargeSI),
      distance: readQuantity(text.r, SMALL_DISTANCE_UNITS, unit.r),
      permittivity: readPlain(eps),
      negative,
    },
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<FieldKey>[] = [
    {
      key: "q",
      label: strings.chargeMagnitude,
      units: CHARGE_UNITS,
      solved: target === "charge",
      hint: strings.magnitudeHint,
    },
    {
      key: "r",
      label: strings.distanceToPoint,
      units: SMALL_DISTANCE_UNITS,
      solved: target === "distance",
    },
    {
      key: "E",
      label: strings.fieldStrength,
      units: FIELD_UNITS,
      solved: target === "field",
    },
  ];
  const solvedSI = {
    q: value === null ? null : Math.abs(value.charge),
    r: value?.distance ?? null,
    E: value?.field ?? null,
  };
  const names = strings.fieldExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = FIELD_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setUnit(ex.unit);
      setNegative(ex.negative);
      setTarget("field");
      setMedium("vacuum");
      setEps("1");
    }
  };

  return (
    <>
      <SelectField
        id="cl-field-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(FIELD_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <PillGroup
        label={strings.solveFor}
        name="cl-field-solve"
        onChange={(next: FieldTarget) => {
          setText(carrySolved(specs, text, unit, solvedSI, sig));
          setTarget(next);
        }}
        options={[
          { value: "field", label: strings.fieldShort },
          { value: "charge", label: strings.chargeShort },
          { value: "distance", label: strings.distanceShort },
        ]}
        value={target}
      />
      <PillGroup
        label={strings.chargeSign}
        name="cl-field-sign"
        onChange={(next: "positive" | "negative") =>
          setNegative(next === "negative")
        }
        options={[
          { value: "positive", label: strings.positive },
          { value: "negative", label: strings.negative },
        ]}
        value={negative ? "negative" : "positive"}
      />
      <MediumFields
        eps={eps}
        idPrefix="cl-field"
        medium={medium}
        onEps={(v) => {
          setMedium("custom");
          setEps(v);
        }}
        onMedium={(id) => {
          setMedium(id);
          if (id in MEDIA) {
            setEps(String(MEDIA[id]));
          }
        }}
        strings={strings}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="cl-field"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={solvedSI}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.fieldStrength}
                value={`${formatSig(fmt, value.field, sig)} N/C`}
              />
              <StatCard
                label={strings.direction}
                value={
                  value.charge < 0
                    ? strings.towardCharge
                    : strings.awayFromCharge
                }
              />
              <StatCard
                label={strings.potential}
                value={`${formatSig(fmt, value.potential, sig)} V`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={pointFieldWorking(target, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.radialCaption}>
              <RadialDiagram
                description={strings.radialDescription}
                id="cl-radial-diagram"
                label={strings.radialLabel}
                negative={value.charge < 0}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}
